import JSZip from "jszip";
import type { EditorSnapshot, SceneDocument } from "../types";

const PROJECT_MIME = "application/x-feral3d";

export async function encodeProject(snapshot: EditorSnapshot): Promise<Uint8Array> {
  const zip = new JSZip();
  zip.file("manifest.json", JSON.stringify({
    format: "feral3d",
    version: 1,
    createdBy: "Feral3D 0.1.0",
    units: snapshot.scene.units,
    revision: snapshot.scene.revision,
  }, null, 2));
  zip.file("scene.json", JSON.stringify(snapshot, null, 2));
  zip.file("README.txt", "Feral3D local project archive. Textures are embedded in scene.json for portable browser and desktop reopening.\n");
  return zip.generateAsync({ type: "uint8array", compression: "DEFLATE", compressionOptions: { level: 6 }, mimeType: PROJECT_MIME });
}

export async function decodeProject(bytes: Uint8Array): Promise<EditorSnapshot> {
  const zip = await JSZip.loadAsync(bytes);
  const manifestText = await zip.file("manifest.json")?.async("text");
  const sceneText = await zip.file("scene.json")?.async("text");
  if (!manifestText || !sceneText) throw new Error("This is not a valid .feral3d project archive.");
  const manifest = JSON.parse(manifestText) as { format?: string; version?: number };
  if (manifest.format !== "feral3d" || manifest.version !== 1) throw new Error("This Feral3D project version is not supported.");
  const snapshot = JSON.parse(sceneText) as EditorSnapshot;
  validateSnapshot(snapshot);
  return snapshot;
}

function validateSnapshot(snapshot: EditorSnapshot): void {
  if (snapshot.scene?.format !== "feral3d" || snapshot.scene?.formatVersion !== 1) throw new Error("The project scene header is invalid.");
  if (!Array.isArray(snapshot.scene.objects) || !Array.isArray(snapshot.scene.materials)) throw new Error("The project scene data is incomplete.");
  for (const object of snapshot.scene.objects) {
    if (!Array.isArray(object.vertices) || !Array.isArray(object.faces)) throw new Error(`Mesh ${object.name ?? object.id} is incomplete.`);
  }
}

export function downloadBytes(bytes: Uint8Array, filename: string, mimeType = "application/octet-stream"): void {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const blob = new Blob([copy.buffer], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const DB_NAME = "feral3d-local";
const STORE_NAME = "recovery";
const RECOVERY_KEY = "last-project";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveRecovery(snapshot: EditorSnapshot): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(JSON.stringify(snapshot), RECOVERY_KEY);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  db.close();
}

export async function loadRecovery(): Promise<EditorSnapshot | null> {
  const db = await openDb();
  const value = await new Promise<string | undefined>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).get(RECOVERY_KEY);
    request.onsuccess = () => resolve(request.result as string | undefined);
    request.onerror = () => reject(request.error);
  });
  db.close();
  if (!value) return null;
  const snapshot = JSON.parse(value) as EditorSnapshot;
  validateSnapshot(snapshot);
  return snapshot;
}

export function sceneSummary(scene: SceneDocument) {
  return {
    format: scene.format,
    revision: scene.revision,
    units: scene.units,
    activeObjectId: scene.activeObjectId,
    objects: scene.objects.map((object) => ({
      id: object.id,
      name: object.name,
      vertexCount: object.vertices.length,
      edgeCount: Object.keys(object.edgeIds).length,
      faceCount: object.faces.length,
      transform: object.transform,
      visible: object.visible,
    })),
    materials: scene.materials.map((material) => ({
      id: material.id,
      name: material.name,
      color: material.color,
      roughness: material.roughness,
      metallic: material.metallic,
      hasBaseColorTexture: Boolean(material.baseColorTextureId),
    })),
    textureCount: scene.textures.length,
    operationCount: scene.operations.length,
    recentOperations: scene.operations.slice(-10),
  };
}

